import React, { useState, useEffect, forwardRef, useImperativeHandle, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import GetCookie from '../../utils/GetCookie';
import { StatusBadgeXSm } from '../../utils/StatusBadge';
import PaginationControls from '../../utils/PaginationControls';
import TrimContent from '../../utils/TrimContent';
import authNavigate from '../../globalComponents/AuthNav';
import { useNavigate } from 'react-router-dom';
import { CategoryCache } from '../../globalComponents/NavigationCache';

const deriveTemplateStatus = (t) => {
  const status = t.status || '';
  const total = t.agent_count || 0;
  const done = t.completed_agents || 0;
  if (!status && t.generation_status === 'COMPLETED') return 'MAPPED';

  if (status === 'COMPLETED' && total > 0) {
    if (done === 0) return '';
    if (done < total) {
      const pct = Math.round((done / total) * 100);
      return `PROGRESS:${Math.min(99, Math.max(1, pct))}`;
    }
  }
  return status;
};
// Same palette + hash function as DataFabricList — keeps tag colors consistent across pages
const TAG_COLORS = [
  '#3b4eff', // blue
  '#4aa3df', // light blue
  '#d65a1f', // orange
  '#7a1e6c', // purple
  '#2E8B57', // sea green
  '#DC3545', // red
  '#e6a700', // amber
  '#0d9488', // teal
];

const getPillColor = (category) => {
  if (!category) return TAG_COLORS[0];
  let hash = 0;
  for (let i = 0; i < category.length; i++) {
    hash = category.charCodeAt(i) + ((hash << 5) - hash);
  }
  return TAG_COLORS[Math.abs(hash) % TAG_COLORS.length];
};

const CategoryMultiSelect = ({
  categories,
  selectedCategories,
  onChange
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const [tempSelectedCategories, setTempSelectedCategories] = useState(selectedCategories);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    setTempSelectedCategories(selectedCategories);
  }, [selectedCategories]);

  const handleToggleCategory = (category) => {
    const newSelection = tempSelectedCategories.includes(category)
      ? tempSelectedCategories.filter(c => c !== category)
      : [...tempSelectedCategories, category];

    setTempSelectedCategories(newSelection);
  };
  const handleSelectAll = () => {
    setTempSelectedCategories(categories);
  };

  const handleDeselectAll = () => {
    setTempSelectedCategories([]);
  };

  // Clear all selections
  const handleClearAll = () => {
    setTempSelectedCategories([]);
    onChange([]);
    setIsOpen(false);
  };

  // Apply the temporary selections and close dropdown
  const handleApply = () => {
    onChange(tempSelectedCategories);
    setIsOpen(false);
  };


  // Cancel and revert to applied selections
  const handleCancel = () => {
    setTempSelectedCategories(selectedCategories);
    setIsOpen(false);
  };

  return (
    <div className='ms-2 position-relative' ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        type="button"
        className="ev-message-card2 ev-btn-xsmall ev-fs-11 "
        onClick={() => setIsOpen(!isOpen)}
      >
        <i className="bi bi-funnel"></i> Category
        {selectedCategories.length > 0 && (
          (` (${selectedCategories.length})`)
        )}
        <i className={`bi bi-chevron-${isOpen ? 'up' : 'down'} ms-1`}></i>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          className="position-absolute ev-fs-11 ev-category-dropdown mt-1">

          {/* --- NEW: Top Buttons (Select All / Deselect All) --- */}
          <div className="d-flex justify-content-end gap-2 mb-2">
            <button
              type="button"
              className="ev-btn-blue-outline-round ev-btn-xsmall"
              onClick={handleSelectAll}
            >
              Select All
            </button>
            <button
              type="button"
              className="ev-btn-blue-outline-round ev-btn-xsmall"
              onClick={handleDeselectAll}
            >
              Deselect All
            </button>
          </div>

          {/* Category List */}
          {categories.length === 0 ? (
            <div className="ev-padding-small text-muted">
              No categories available
            </div>
          ) : (
            categories.map((category) => (
              <div
                key={category}
                className="ev-btn-menu-action cursor-pointer justify-content-start gap-2 mb-1"
                onClick={() => handleToggleCategory(category)}
              >
                <input
                  type="checkbox"
                  checked={tempSelectedCategories.includes(category)}
                  onChange={() => { }}
                  className="cursor-pointer"
                />
                <span className="ev-xsmall-p-dark">{category}</span>
              </div>
            ))
          )}

          {/* Action Buttons */}
          <div className="d-flex justify-content-end gap-2 mt-2 pt-2">
            <div className='w-190'></div>
            <button
              type="button"
              className="btn ev-fs-11 btn-sm btn-primary"
              onClick={handleApply}
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Tags display on fabric card (matches dashboard FabricTagsDisplay styling) ─
const FabricTagsDisplay = ({ tags }) => {
  const tagList = Array.isArray(tags) ? tags.filter(Boolean) : [];
  if (tagList.length === 0) return null;

  return (
    <div className="d-flex flex-wrap gap-1 mt-2 mb-2">
      {tagList.map(tag => (
        <span
          key={tag}
          className="ev-bold-reg py-1 px-2 ev-radius-large ev-fs-11"
          style={{
            background: `${getPillColor(tag)}33`,
            color: getPillColor(tag),
            border: `1.5px solid ${getPillColor(tag)}`,
          }}
        >
          <i className="bi bi-tag me-1"></i>
          <TrimContent text={tag} maxLength={20} />
        </span>
      ))}
    </div>
  );
};

const GenbiTemplateList = forwardRef(({
  categoryFilter,
  selectedFabricId,
  onClearSelectedFabric,
  onEditTemplate,
  onOpenTemplate,
  onRefresh,
  onDeleteTemplate,
  onDeleteRequest,
  openTemplateIds,
  onError,
  isFormVisible,
  editingTemplateId,
  editingTemplateName,
  isFormDirty,
  onSaveTemplate,
  onCanCreateChange,
  isCollapsed = false,
  onToggleCollapse,
}, ref) => {
  const [templates, setTemplates] = useState([]);
  const [expandedFabrics, setExpandedFabrics] = useState({});
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(25);
  // const skipInitialFetch = useRef(
  //   // eslint-disable-next-line eqeqeq
  //   !!sessionStorage.getItem('genbi_fabric_id') && selectedFabricId != Number(sessionStorage.getItem('genbi_fabric_id'))
  // );


    // Skip the mount fetch while the store has not caught up with sessionStorage
  // yet, so the fetch effect below fires once — on the dep change — instead of
  // once now with a wrong id and again once GenbiDashboard's mount effect
  // reconciles. The test is "the two disagree", NOT "the store is empty": a
  // stale selectedFabricId left over from the previously opened project is
  // just as wrong as a null one, and !selectedFabricId misses it. Evaluated
  // once, on first render — useRef discards later values — so it has to be
  // right the first time.
  const skipInitialFetch = useRef((() => {
    const pendingFabricId = sessionStorage.getItem('genbi_fabric_id');
    return !!pendingFabricId && Number(pendingFabricId) !== Number(selectedFabricId);
  })());

  
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const hasFetchedRef = useRef(false);
  const [localCategoryFilter, setLocalCategoryFilter] = useState([]);
  const [fabricCategories, setFabricCategories] = useState([]);
  const [showDirtyModal, setShowDirtyModal] = useState(false);
  const [pendingEdit, setPendingEdit] = useState(null);
  // Tags active in DataFabricList when user clicked a project card.
  // Read once on mount, immediately cleared from sessionStorage so it doesn't persist.
  const [activeTagsFromNav] = useState(() => {
    try {
      const saved = sessionStorage.getItem('genbi_active_tags');
      if (saved) {
        sessionStorage.removeItem('genbi_active_tags');
        return JSON.parse(saved);
      }
    } catch (_) {}
    return [];
  });

  const navigate = useNavigate();

  const API_BASE_URL = '/pceapi/genbi';
  const headers = {
    Authorization: GetCookie('access_token')
  };

  const fetchTemplates = async () => {
    setIsLoading(true);

    try {
      const requestBody = {
        page,
        size,
        search: search || undefined
      };

      // Filter by specific fabric when navigated from Dashboard card click
      if (selectedFabricId) {
        requestBody.filter_fabric_id = selectedFabricId;
      }

      const response = await axios.post(
        `${API_BASE_URL}/list-templates`,
        requestBody,
        { headers }
      );

      const data = response.data;

      if (data.status === 'NotAuthorized') {
        authNavigate('/login', 'iam', navigate);
        return;
      }

      if (data.status === 'failed') {
        onError(data.message || 'Failed to load presets');
        return;
      }

      setTemplates(data.data || []);
      setTotal(data.total || 0);
    } catch (error) {
      console.error('Error fetching templates:', error);
      onError(error.message || 'Failed to load presets');
    } finally {
      hasFetchedRef.current = true;
      setIsLoading(false);
    }
  };
  const fetchCategories = async () => {
    const cachedCats = CategoryCache.get('hta_categories');
    if (cachedCats) {
      setFabricCategories(cachedCats);
      return;
    }

    try {
      const response = await axios.post(
        '/pceapi/fabrics/list',
        { page: 1, size: 1 },
        { headers }
      );

      if (response.data.status === 'success') {
        const cats = response.data.categories || ['General'];
        setFabricCategories(cats);
        CategoryCache.set('hta_categories', cats);
      }
    } catch (error) {
      console.error('TemplateList Category Fetch Error:', error);
    }
  };

  const activeCategories =
    localCategoryFilter.length > 0
      ? localCategoryFilter
      : categoryFilter;

  const filteredFabrics = useMemo(() => {
    let filtered = templates;

    // Filter by specific fabric when navigated from Dashboard card click.
    // When a fabric is selected by ID we skip the category filter entirely —
    // the fabric is already isolated and applying an unrelated category filter
    // (e.g. a stale sidebar selection) would wrongly hide it.
    if (selectedFabricId) {
      // eslint-disable-next-line eqeqeq
      const result = filtered.filter(fabric => fabric.id == selectedFabricId);
      return result;
    }

    // No specific fabric selected — apply category filter normally
    if (activeCategories.length > 0) {
      filtered = filtered.filter(fabric => {
        const fabricCategories = (Array.isArray(fabric.category) && fabric.category.length > 0)
          ? fabric.category
          : ['General'];
        return fabricCategories.some(cat => activeCategories.includes(cat));
      });
    }

    return filtered;
  }, [templates, activeCategories, selectedFabricId]);

  // Collect tags to display above the template list.
  // If user navigated from DataFabricList with active tag filters, show only those tags.
  // Otherwise collect every unique tag across the currently visible fabrics.
  const allUniqueTags = useMemo(() => {
    if (activeTagsFromNav.length > 0) {
      return activeTagsFromNav;
    }
    const set = new Set();
    filteredFabrics.forEach(fabric => {
      const tags = (Array.isArray(fabric.category) && fabric.category.length > 0)
        ? fabric.category
        : ['General'];
      tags.forEach(t => set.add(t));
    });
    return Array.from(set);
  }, [filteredFabrics, activeTagsFromNav]);

  useImperativeHandle(ref, () => ({
    fetchTemplates
  }));

  // Fetch templates when component mounts or when pagination/search/fabric changes
  useEffect(() => {
    if (skipInitialFetch.current) {
      skipInitialFetch.current = false;
      return;
    }
    fetchTemplates();
  }, [page, size, search, selectedFabricId]);

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    if (!onCanCreateChange) return;
    if (!hasFetchedRef.current || isLoading) return;
    const selected = selectedFabricId
      ? templates.find(fabric => fabric.id === Number(selectedFabricId))
      : null;
    onCanCreateChange(
      selected
        ? Boolean(selected.can_create_template)
        : templates.some(fabric => fabric.can_create_template)
    );
  }, [templates, selectedFabricId, onCanCreateChange, isLoading]);

  // Auto-expand fabric when navigated from Dashboard (Project Details click)
  useEffect(() => {
    if (selectedFabricId) {
      setExpandedFabrics(prev => ({
        ...prev,
        [Number(selectedFabricId)]: true
      }));
    }
  }, [selectedFabricId]);

  // Toggle fabric expansion
  const toggleFabricExpansion = (fabricId) => {
    setExpandedFabrics(prev => ({
      ...prev,
      [fabricId]: !prev[fabricId]
    }));
  };

  // Handle search input change
  const handleSearchInputChange = (e) => {
    setSearchInput(e.target.value);
  };

  // Handle search button click
  const handleSearch = () => {
    setSearch(searchInput);
    setPage(1);
  };

  // Handle Enter key press in search input
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Handle edit button click
  const handleEditClick = async (template, fabricId, fabricName) => {
    if (isFormVisible && isFormDirty) {
      setPendingEdit({ template, fabricId, fabricName });
      setShowDirtyModal(true);
      return;
    }
    try {
      await onEditTemplate(template, fabricId, fabricName);
    } catch (error) {
      onError('Failed to load Preset details');
    }
  };

  const handleConfirmDiscard = async () => {
    setShowDirtyModal(false);
    if (pendingEdit) {
      try {
        await onEditTemplate(pendingEdit.template, pendingEdit.fabricId, pendingEdit.fabricName);
      } catch (error) {
        onError('Failed to load Preset details');
      }
      setPendingEdit(null);
    }
  };

  const handleConfirmSave = () => {
    setShowDirtyModal(false);
    if (onSaveTemplate) onSaveTemplate(pendingEdit);
    setPendingEdit(null);
  };

  // Handle delete click
  const handleDeleteClick = (template, fabricId, fabricName) => {
    if (onDeleteRequest) {
      onDeleteRequest(template, fabricId, fabricName);
    }
  };
  const getCategoryColorClass = (category) => {
    if (!category) return 'category-badge-1';

    let hash = 0;
    for (let i = 0; i < category.length; i++) {
      hash = category.charCodeAt(i) + ((hash << 5) - hash);
    }

    const colorIndex = (Math.abs(hash) % 7) + 1;
    return `category-badge-${colorIndex}`;
  };
  const categoryCountMap = useMemo(() => {
    const map = {};

    templates.forEach((fabric) => {
      const cats = (Array.isArray(fabric.category) && fabric.category.length > 0)
        ? fabric.category
        : ['General'];
      cats.forEach((category) => {
        map[category] = (map[category] || 0) + 1;
      });
    });

    return map;
  }, [templates]);

  return (
    <div className={`sidebar-inner ev-card-2 genbi-template-sidebar ${isCollapsed ? 'genbi-template-sidebar--collapsed' : ''}`}>
      <div className="d-flex align-items-center gap-1 genbi-template-sidebar-toolbar">
        {/* <p className="ev-reg-p-dark ev-bold-reg mb-0"> 
           Templates <span>({(selectedFabricId || activeCategories.length > 0) ? filteredFabrics.length : total} Projects)</span> 
        </p> */}
        {/* {(activeCategories.length > 0 ? activeCategories : sortedCategories).map(category => (
          <div key={category} className={`category-badge ${getCategoryColorClass(category)}`}>
            {category} 
          </div>
        ))} */}

        <button
          className="ev-btn-lightblue-fill-round ev-bold-reg ev-btn-small genbi-template-refresh-btn"
          type="button"
          onClick={fetchTemplates}
          disabled={isLoading}
        >
          <i className="bi bi-arrow-repeat"></i><span className="genbi-template-refresh-text"> Refresh</span>
        </button>
        <button
          type="button"
          className="ev-btn-lightblue-fill-round ev-bold-reg ev-btn-small ms-auto genbi-template-collapse-btn"
          onClick={onToggleCollapse}
          aria-label={isCollapsed ? 'Expand preset list' : 'Collapse preset list'}
          aria-expanded={!isCollapsed}
        >
          <i className={`bi ${isCollapsed ? 'bi-chevron-right' : 'bi-chevron-left'}`}></i>
        </button>
      </div>

      <div className="d-flex justify-content-between align-items-center mb-2">
        {/* <div className=" position-relative flex-grow-1">
          <input
            type="text"
            className="form-control search-input ms-0"
            placeholder="Search Preset"
            value={searchInput}
            onChange={handleSearchInputChange}
            onKeyPress={handleKeyPress}
          />
          <button
            className="ev-btn-lightblue-fill-round search-bar-button"
            onClick={handleSearch}
          >
            <i className="bi bi-search"></i>
          </button>
        </div>
        <div className="flex-shrink-0">
          <CategoryMultiSelect
            categories={fabricCategories}
            selectedCategories={localCategoryFilter}
            onChange={(newSelection) => {
              setLocalCategoryFilter(newSelection);
              if (onClearSelectedFabric) onClearSelectedFabric();
            }}
          />
        </div> */}
      </div>

      <div className="tree-container mt-2 scrollable-element genbi-template-tree-container vh-70">

        {isLoading && (
          <div className="text-center p-3 mt-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        )}

        {!isLoading && !filteredFabrics.some(fabric => fabric.templates.length > 0) && (
          <p className="ev-small-p-blue text-center p-3">Click <span className="ev-btn-yellow-fill-round ev-bold-reg ev-btn-xsmall">Import Preset</span> button above to get started.</p>
        )}

        {/* {!isLoading && allUniqueTags.length > 0 && (
          <FabricTagsDisplay tags={allUniqueTags} />
        )} */}

        {!isLoading && filteredFabrics.some(fabric => fabric.templates.length > 0) && (
          <div className="tree-container-inner">
            {filteredFabrics.filter(fabric => fabric.templates.length > 0).map(fabric => (
              <div key={fabric.id} className="mb-3">
                <div className="ev-card-1 ev-padding-small mb-2 ev-radius-small">
                  <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center">
                      <button
                        className="btn btn-sm p-0 me-2 ev-fs-20"
                        onClick={() => toggleFabricExpansion(fabric.id)}
                      >
                        <i className={`ev-reg-p-dark bi ${expandedFabrics[fabric.id] ? 'bi-chevron-down' : 'bi-chevron-right'}`}></i>
                      </button>
                      <span className="ev-small-p-blue ev-bold-med fabric-name-wrap d-flex align-items-start gap-1 avatar-tooltip" data-tooltip={fabric.name}>
                        <i className="bi bi-boxes" ev-message-card2></i>
                        <span>{fabric.name}</span>
                      </span>
                    </div>
                    <span className="ev-message-card2 ev-radius-med ev-fs-11 ev-bold-med">
                      {fabric.template_count} Dossier Preset
                    </span>
                  </div>

                  {expandedFabrics[fabric.id] && (
                    <div>
                      <p className="ev-small-p-blue mb-1 mt-2 ev-bold-med">Dossier Preset</p>
                      {fabric.templates.map(template => (
                        <div
                          key={template.id}
                          className={`${editingTemplateId === template.id ? 'ev-card-5' : 'ev-card-9'} template-card-hover-effect mt-2 ev-radius-small`}
                          style={{ cursor: 'pointer' }}
                          onClick={() => onOpenTemplate(template, fabric.id, fabric.name)}
                        >
                          <div className="mb-2 text-end">
                                {/* <span className="ev-message-card3 ev-btn-small ev-xsmall-p-dark mb-1 me-2">
                                  <i className="bi bi-robot"></i> Agents: {template.agent_count || 0}
                                </span> */}

                                {/* <button
                                  className={`ev-btn-blue-fill-round ev-btn-small ev-bold-reg ${openTemplateIds?.includes(template.id) ? 'disabled' : ''}`}
                                  onClick={(e) => { e.stopPropagation(); onOpenTemplate(template, fabric.id, fabric.name); }}
                                  disabled={openTemplateIds?.includes(template.id)}
                                >
                                  {openTemplateIds?.includes(template.id) ? <><i className="bi bi-dash-circle" />{' '}Opened</> : <><i className="bi bi-plus-circle" />{' '}Open</>}
                                </button> */}

                                <StatusBadgeXSm status={deriveTemplateStatus(template)} />

                                {fabric.can_edit_template && (
                                  <button
                                    className="ev-btn-skyblue-fill-round ev-radius-xsmall pe-2 ps-2 ms-2 avatar-tooltip"
                                    onClick={(e) => { e.stopPropagation(); handleEditClick(template, fabric.id, fabric.name); }}
                                    data-tooltip={template.status === 'RUNNING' ? 'Preset/Report Running' : 'Edit Preset'}
                                    disabled={template.status === 'RUNNING' || template.report_gen_status === 'RUNNING'}
                                  >
                                    <i className="bi bi-pencil"></i>
                                  </button>
                                )}

                                {fabric.can_delete_template && (
                                  <button
                                    className="ev-btn-red-fill-round ev-radius-xsmall pe-2 ps-2 ms-2 avatar-tooltip"
                                    onClick={(e) => { e.stopPropagation(); handleDeleteClick(template, fabric.id, fabric.name); }}
                                    data-tooltip={template.status === 'RUNNING' ? 'Preset Running' : 'Delete Preset'}
                                    disabled={template.status === 'RUNNING'}
                                  >
                                    <i className="bi bi-trash"></i>
                                  </button>
                                )}
                              </div>
                              <div className="d-flex align-items-center mb-0">
                                <span className="ev-small-p-dark flex-grow-1 me-2 avatar-tooltip" data-tooltip={template.template_name}>
                                  <i className="bi bi-kanban"></i>
                                  &nbsp;
                                  <TrimContent text={template.template_name} maxLength={20} />
                                  &nbsp;
                                </span>
                                {openTemplateIds?.includes(template.id) && (
                                  <span className="template-card-open-tag flex-shrink-0">Opened</span>
                                )}
                              </div>
                            </div>
                          ))}
                  </div>
                  )}
                  </div>
                </div>
              ))}
          </div>
        )}
      </div>

      {/* Pagination for sidebar */}
      {total > size && <div className="d-flex justify-content-between align-items-center mt-3 sidebar-inner-bottom-fix-ele ev-fs-12">
        <div>
          <button
            className="ev-btn-light-fill ev-btn-xsmall"
            disabled={page === 1 || isLoading}
            onClick={() => setPage(page - 1)}
          >
            <i className="bi bi-chevron-left"></i>
          </button>

          <span className="ev-xsmall-p-dark ms-2">
            Page {page} of {Math.ceil(total / size) || 1}
          </span>

          <button
            className="ev-btn-light-fill ev-btn-xsmall ms-2"
            disabled={page >= Math.ceil(total / size) || isLoading}
            onClick={() => setPage(page + 1)}
          >
            <i className="bi bi-chevron-right"></i>
          </button>
        </div>
        <div>
          <PaginationControls
            initialSize={size}
            onSizeChange={(newSize) => {
              setSize(newSize);
              setPage(1);
            }}
          />
        </div>
      </div>}

      {/* Unsaved Changes Modal */}
      {showDirtyModal && ReactDOM.createPortal(
        <div className="popup" onClick={() => setShowDirtyModal(false)}>
          <div className="ev-card-4 ev-dirty-modal p-0" onClick={(e) => e.stopPropagation()}>
            <div className="comment-success-modal-header">
              <p className="mb-0 ev-fs-15 ev-bold-med ev-reg-p-dark">
                Unsaved Changes
              </p>
              <button
                type="button"
                className="ev-btn-text-only-fill ev-fs-20"
                onClick={() => setShowDirtyModal(false)}
              >
                <i className="bi bi-x"></i>
              </button>
            </div>
            <div className="comment-success-modal-body ev-fs-12">
              <p className="ev-reg-p-dark mb-2">
                You have unsaved changes in the current preset: <strong>{editingTemplateName}</strong>.
              </p>
              <p className="ev-reg-p-dark mb-1">
                Would you like to save them before switching to another preset?
              </p>
              <p className="text-danger mb-0">
                Discarding will permanently lose all unsaved changes.
              </p>
            </div>
            <div className="comment-success-modal-footer justify-content-between">
              <button
                type="button"
                className="ev-btn-red-fill-round ev-bold-reg px-3"
                onClick={handleConfirmDiscard}
              >
                <i className="bi bi-x me-1"></i>
                Discard
              </button>
              <button
                type="button"
                className="ev-btn-blue-fill-round ev-bold-reg px-3"
                onClick={handleConfirmSave}
              >
                <i className="bi bi-floppy me-1"></i>
                Save
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
});

GenbiTemplateList.displayName = 'GenbiTemplateList';

export default GenbiTemplateList;
